import type { PartnerSchedulingClientStatus } from '@nsc/workflows-partner-scheduling/entities/PartnerSchedulingClientStatus.js';
import { normalizeSchedulingKey } from '@nsc/workflows-partner-scheduling/ticketing/partner-scheduling-patterns.js';
import { html, nothing } from 'lit';

export type ClientSiteRecord = {
  clientKey?: string | null;
  clientName?: string | null;
  region?: string | null;
  country?: string | null;
  city?: string | null;
  address?: string | null;
  zipCode?: string | null;
  partners?: string[] | null;
};

export type PartnerContact = {
  partnerName?: string | null;
  contactEmail?: string | null;
};

type SitePartner = { partnerName: string; contactEmail: string };

function resolveSitePartners(
  partnerNames: ClientSiteRecord['partners'],
  partnersByKey: Map<string, PartnerContact>,
): SitePartner[] {
  return (partnerNames ?? []).map(name => {
    const partner = partnersByKey.get(normalizeSchedulingKey(name));
    return {
      partnerName: name?.trim() || '—',
      contactEmail: partner?.contactEmail?.trim() || '—',
    };
  });
}

function siteLabel(record: ClientSiteRecord, index: number): string {
  const parts = [record.city, record.country, record.region].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : `Site ${index + 1}`;
}

export function PartnerSchedulingClientSitesModal(props: {
  client: Pick<PartnerSchedulingClientStatus, 'clientName' | 'clientKey'> | null;
  sites: ClientSiteRecord[];
  partnersByKey: Map<string, PartnerContact>;
  onClose: () => void;
}) {
  const { client, sites, partnersByKey, onClose } = props;
  if (!client) return nothing;

  const title = client.clientName ?? client.clientKey ?? 'Client';

  return html`
    <dialog class="modal modal-open">
      <div class="modal-box max-w-4xl w-full">
        <h2 class="text-xl font-bold text-neutral">${title}</h2>
        <p class="text-sm text-base-content/60 mt-1">
          ${sites.length} workbook site${sites.length === 1 ? '' : 's'} in partner-scheduling-clients
        </p>

        <div class="mt-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          ${sites.length === 0
            ? html`<p class="text-base-content/60 py-6 text-center">No workbook sites found for this client.</p>`
            : sites.map(
                (site, index) => html`
                  <section class="rounded-xl border border-neutral/15 bg-base-200/30 p-4">
                    <h3 class="font-semibold text-neutral mb-3">${siteLabel(site, index)}</h3>
                    <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm mb-4">
                      <div>
                        <dt class="text-base-content/50">Region</dt>
                        <dd>${site.region ?? '—'}</dd>
                      </div>
                      <div>
                        <dt class="text-base-content/50">Country</dt>
                        <dd>${site.country ?? '—'}</dd>
                      </div>
                      <div>
                        <dt class="text-base-content/50">City</dt>
                        <dd>${site.city ?? '—'}</dd>
                      </div>
                      <div>
                        <dt class="text-base-content/50">Zip code</dt>
                        <dd>${site.zipCode ?? '—'}</dd>
                      </div>
                      <div class="sm:col-span-2">
                        <dt class="text-base-content/50">Address</dt>
                        <dd>${site.address ?? '—'}</dd>
                      </div>
                    </dl>
                    ${(site.partners?.length ?? 0) > 0
                      ? html`
                          <div class="overflow-x-auto">
                            <table class="table table-xs">
                              <thead>
                                <tr>
                                  <th>Partner</th>
                                  <th>Email</th>
                                </tr>
                              </thead>
                              <tbody>
                                ${resolveSitePartners(site.partners, partnersByKey).map(
                                  partner => html`
                                    <tr>
                                      <td>${partner.partnerName}</td>
                                      <td class="font-mono text-xs">${partner.contactEmail}</td>
                                    </tr>
                                  `,
                                )}
                              </tbody>
                            </table>
                          </div>
                        `
                      : html`<p class="text-sm text-base-content/50">No partners assigned.</p>`}
                  </section>
                `,
              )}
        </div>

        <div class="modal-action">
          <button type="button" class="btn" @click=${onClose}>Close</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop" @submit=${(e: Event) => (e.preventDefault(), onClose())}>
        <button type="submit">close</button>
      </form>
    </dialog>
  `;
}

export function groupClientRecordsByKey(records: ClientSiteRecord[] | undefined) {
  const byKey = new Map<string, ClientSiteRecord[]>();
  for (const record of records ?? []) {
    const key = normalizeSchedulingKey(record.clientKey || record.clientName || '');
    if (!key) continue;
    const bucket = byKey.get(key) ?? [];
    bucket.push(record);
    byKey.set(key, bucket);
  }
  for (const bucket of byKey.values()) {
    bucket.sort((a, b) =>
      [a.region, a.country, a.city, a.address]
        .join('\0')
        .localeCompare([b.region, b.country, b.city, b.address].join('\0')),
    );
  }
  return byKey;
}

export function indexPartnersByKey(partners: PartnerContact[] | undefined) {
  const byKey = new Map<string, PartnerContact>();
  for (const partner of partners ?? []) {
    const key = normalizeSchedulingKey(partner.partnerName ?? '');
    if (key && !byKey.has(key)) byKey.set(key, partner);
  }
  return byKey;
}
